import { Table, TableColumn } from '@ibsys/kit';
import { Tooltip } from '@/shared/ui';
import { CircleHelpIcon } from 'lucide-react';
import { FC, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { withSecondsSuffix } from '@/entities/attributes';
import type { ConversationAttrExplanationStep } from '@/entities/conversations';
import { renderConversationAttrValue } from '@/entities/conversations/model/utils';
import { useTimeDisplayFormat } from '@/entities/settings';
import { cn } from '@/shared/lib';
import type { AttributeDefinitionsMap, ContentRow, NodeContent } from '../../model';
import { getTablesFromNestedArray, buildContentRowsFromObject } from '../../lib';
import { AttrCommentText } from './AttrCommentText';
import { AttrExplanationPopover } from './AttrExplanationPopover';
import styles from './dialogDataTables.module.css';

type Props = {
  content: NodeContent;
  attributeDefinitions?: AttributeDefinitionsMap;
};

const isMultiTable = (content: NodeContent): content is ContentRow[][] =>
  Array.isArray(content[0]) && Array.isArray(content[0]);

const hasExpandableRows = (rows: ContentRow[]): boolean =>
  rows.some((r) => (r.nestedArrays?.length ?? 0) > 0 || (r.nestedObjects?.length ?? 0) > 0);

const isRowExpandable = (row: ContentRow): boolean =>
  (row.nestedArrays?.length ?? 0) > 0 || (row.nestedObjects?.length ?? 0) > 0;

type ProcessedRow = ContentRow & {
  /** Название атрибута для показа: у интервала времени в секундах — с подписью единицы */
  displayName: string;
  /** Предвычисленное отрендеренное значение атрибута */
  renderedValue: ReactNode;
  /** Комментарий к значению атрибута; пустая строка если отсутствует */
  comment: string;
  /** Подсказка к атрибуту из справочника; null — иконка «?» не отображается */
  tooltip: string | null;
  /** Шаги разбора вычисления значения; null — иконка разбора не отображается */
  explanation: ConversationAttrExplanationStep[] | null;
  /** Момент вычисления значения по правилу; null — не известен */
  computedAt: string | null;
};

const ContentTable: FC<{
  rows: ContentRow[];
  attributeDefinitions?: AttributeDefinitionsMap;
  expandable?: boolean;
}> = ({ rows, attributeDefinitions, expandable }) => {
  const { t } = useTranslation(['dialogs', 'common']);
  const timeDisplayFormat = useTimeDisplayFormat();

  const processedRows = useMemo<ProcessedRow[]>(
    () =>
      rows.map((row) => {
        const leaf = row.attrValue as {
          attributeTooltip?: string | null;
          attributeExplanation?: ConversationAttrExplanationStep[] | null;
          attributeComputedAt?: string | null;
        };
        const rawTooltip = leaf.attributeTooltip;
        const rawExplanation = leaf.attributeExplanation;
        // Убираем индексы массивов из пути (например, "a[0].b" → "a.b"),
        // чтобы корректно найти определение атрибута в attributeDefinitions
        const definition = attributeDefinitions?.get(row.path.replace(/\[\d+\]/g, ''));
        return {
          ...row,
          displayName: withSecondsSuffix(row.name, definition, timeDisplayFormat, t('common:secondsSuffix')),
          renderedValue: renderConversationAttrValue(row.attrValue, definition, timeDisplayFormat),
          comment: typeof row.attrValue.attributeComment === 'string' ? row.attrValue.attributeComment : '',
          tooltip: typeof rawTooltip === 'string' && rawTooltip.length > 0 ? rawTooltip : null,
          explanation: Array.isArray(rawExplanation) && rawExplanation.length > 0 ? rawExplanation : null,
          computedAt: typeof leaf.attributeComputedAt === 'string' ? leaf.attributeComputedAt : null,
        };
      }),
    [rows, attributeDefinitions, timeDisplayFormat, t],
  );

  /**
   * Ширина столбца иконок по фактическому содержимому таблицы: null — иконок нет
   * ни в одной строке и столбец не нужен вовсе, 56 — есть разбор (иконка-кнопка
   * шире и может соседствовать с подсказкой), 32 — только подсказки.
   * Фиксированная ширина на все таблицы забирала бы место у значения там, где
   * иконок нет, — заметнее всего на узкой раскладке (разделённый экран).
   */
  const iconsColumnSize = useMemo<number | null>(() => {
    if (processedRows.some((row) => row.explanation)) return 56;
    return processedRows.some((row) => row.tooltip) ? 32 : null;
  }, [processedRows]);

  const columns: TableColumn<ProcessedRow>[] = useMemo(
    () => [
      {
        accessorKey: 'displayName' as const,
        header: t('dialogs:tableSection.attributeName'),
        size: 200,
        cellClassName: () => 'min-w-0 whitespace-normal wrap-break-word',
        cell: ({ row }) => row.original.displayName,
      },
      {
        accessorKey: 'renderedValue' as const,
        header: t('dialogs:tableSection.attributeValue'),
        minSize: 200,
        cellClassName: () => 'min-w-0 whitespace-normal wrap-break-word',
        cell: ({ row }) => {
          const { renderedValue, comment } = row.original;
          if (!comment) return renderedValue;
          return (
            <div className="flex flex-col">
              <span>{renderedValue}</span>
              <span className="text-xs text-muted-foreground wrap-break-word whitespace-normal">
                <AttrCommentText comment={comment} />
              </span>
            </div>
          );
        },
      },
      ...(iconsColumnSize === null
        ? []
        : [
            {
              id: 'tooltip',
              accessorKey: 'tooltip' as const,
              header: '',
              size: iconsColumnSize,
              enableSorting: false,
              cell: ({ row }) => {
                const { tooltip, explanation, computedAt } = row.original;
                if (!tooltip && !explanation) return null;
                // cursor-default перекрывает дефолтный cursor: help от Tooltip
                return (
                  <span className="inline-flex w-full justify-end items-center gap-1 cursor-default">
                    {tooltip && (
                      <Tooltip tooltip={tooltip}>
                        <CircleHelpIcon
                          className="size-3.5 text-muted-foreground shrink-0 cursor-default"
                          aria-label={tooltip}
                        />
                      </Tooltip>
                    )}
                    {explanation && <AttrExplanationPopover steps={explanation} computedAt={computedAt} />}
                  </span>
                );
              },
            } satisfies TableColumn<ProcessedRow>,
          ]),
    ],
    [t, iconsColumnSize],
  );

  const getRowContent = useCallback(
    (row: ContentRow) => {
      if (!isRowExpandable(row)) return null;
      return (
        <div className="flex flex-col gap-4 p-4">
          {row.nestedArrays?.map((nested) => {
            const tables = getTablesFromNestedArray(nested, row.path);
            return (
              <div key={nested.key} className="flex flex-col gap-4">
                <div className="text-sm font-medium text-muted-foreground">{nested.name}</div>
                {tables.map((tableRows, tableIdx) =>
                  tableRows.length > 0 ? (
                    <ContentTable
                      key={tableIdx}
                      rows={tableRows}
                      attributeDefinitions={attributeDefinitions}
                      expandable={hasExpandableRows(tableRows)}
                    />
                  ) : null,
                )}
              </div>
            );
          })}
          {row.nestedObjects?.map((obj) => {
            const objRows = buildContentRowsFromObject(obj.attrValue, `${row.path}.${obj.key}`);
            if (objRows.length === 0) return null;
            return (
              <div key={obj.key} className="flex flex-col gap-2">
                <div className="text-sm font-medium text-muted-foreground">{obj.name}</div>
                <ContentTable
                  rows={objRows}
                  attributeDefinitions={attributeDefinitions}
                  expandable={hasExpandableRows(objRows)}
                />
              </div>
            );
          })}
        </div>
      );
    },
    [attributeDefinitions],
  );

  return (
    <Table
      type="simple"
      data={processedRows}
      columns={columns}
      getKey={(row) => row.id}
      {...(expandable && {
        getCanBeExpanded: isRowExpandable,
        getRowContent,
        defaultExpandedAll: false,
      })}
      className="w-full mb-4 last:mb-0"
      classNames={{ table: cn('w-auto', styles.valuesTable) }}
      hideHeader
      virtualization={{ enable: false }}
      styles={{ size: 'sm' }}
    />
  );
};

export const ContentPanel: FC<Props> = ({ content, attributeDefinitions }) => {
  const { t } = useTranslation(['dialogs']);
  const tables = isMultiTable(content) ? content : [content];
  if (tables.length === 0 || tables.every((rows) => rows.length === 0)) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground text-sm">{t('dialogs:noData')}</div>
    );
  }
  return (
    <div className="flex flex-col gap-6">
      {tables.map((rows, idx) => (
        <ContentTable
          key={idx}
          rows={rows}
          attributeDefinitions={attributeDefinitions}
          expandable={hasExpandableRows(rows)}
        />
      ))}
    </div>
  );
};
